import {
  Duration,
  FormatDistanceToken,
  FormatDurationOptions,
  format as dateFnsFormat,
  formatDistance,
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
  isValid,
} from "date-fns";

// Dates at or before this are considered invalid (e.g. new Date(null) = epoch)
const MIN_VALID_DATE = new Date("1971-01-01T00:00:00.000Z");

export const parseDate = (
  value: string | Date | number | null | undefined,
): Date | null => {
  if (value === null || value === undefined) return null;
  const date =
    value instanceof Date ? value : new Date(value as string | number);
  if (!isValid(date) || date <= MIN_VALID_DATE) return null;
  return date;
};

export const formatDate = (
  value: string | Date | number | null | undefined,
  dateFormat: string = "dd/MM/yyyy HH:mm",
): string => {
  const date = parseDate(value);
  if (!date) return "";
  return dateFnsFormat(date, dateFormat);
};

export const formatDateRelative = (
  value: string | Date | number | null | undefined,
  options?: { addSuffix?: boolean; includeSeconds?: boolean },
): string => {
  const date = parseDate(value);
  if (!date) return "";
  return formatDistanceToNow(date, options);
};

export const formatDateDistance = (
  dateLeft: string | Date | number | null | undefined,
  dateRight: string | Date | number | null | undefined,
  options?: { addSuffix?: boolean; includeSeconds?: boolean },
): string => {
  const left = parseDate(dateLeft);
  const right = parseDate(dateRight);
  if (!left || !right) return "";
  return formatDistance(left, right, options);
};

export const datePlusDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const dateStart = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const dateEnd = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

export const formatSecondsToDuration = (
  seconds: number,
  options?: FormatDurationOptions & { style?: "short" },
): string => {
  if (options?.style === "short") {
    const formatDistanceLocale: Partial<Record<FormatDistanceToken, string>> = {
      xSeconds: "{{count}}",
      xMinutes: "{{count}}",
      xHours: "{{count}}",
    };
    const shortEnLocale = {
      formatDistance: (token: FormatDistanceToken, count: number) => {
        let val = (count || "00").toString();
        if (val.length < 2 && options.zero) {
          val = "0" + val;
        }
        return formatDistanceLocale[token]?.replace("{{count}}", val) || "";
      },
    };
    options.locale = shortEnLocale;
    options.delimiter = ":";
  }

  return formatDuration(
    intervalToDuration({ start: 0, end: seconds * 1000 }),
    options,
  );
};
